import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, getFirestore, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { compareUnitNumbers } from '../../utils/unitSort';
import type { BankTransactionRow } from '../today/collections';
import { normalizeDefinitionSteps, type WorkflowCase, type WorkflowDefinitionStep } from '../today/workflowBoard';

export type UnitDoc = { id: string; unitNumber?: string | null; unitAmount?: number | null; bookingStatus?: string | null; activeBookingId?: string | null; [key: string]: any };
export type CustomerDoc = { id: string; kycCount: number; [key: string]: any };
export type UploadedDoc = { id: string; type?: string | null; templateId?: string | null; files?: Array<{ name?: string | null; originalName?: string | null; url?: string | null; uploadedAtMs?: number | null }>; createdAt?: any; updatedAt?: any };
export type ChangeOrderDoc = { id: string; coNo?: string; title?: string; category?: string; agency?: string; status?: string; customerApproval?: string; costImpact?: boolean; createdAt?: any; updatedAt?: any; [key: string]: any };

/**
 * Everything the unit page shows for one unit. Live where staff edit things from this page
 * (units, receipts, workflow cases); the rest reloads when `reload()` is called after a drawer closes.
 */
export function useUnitPageData(projectId: string | null, unitId: string | undefined) {
  const db = useMemo(() => getFirestore(), []);
  const [units, setUnits] = useState<UnitDoc[]>([]);
  const [unitsLoaded, setUnitsLoaded] = useState(false);
  const [projectMeta, setProjectMeta] = useState<Record<string, any> | null>(null);
  const [mainSteps, setMainSteps] = useState<WorkflowDefinitionStep[]>([]);
  const [loanSteps, setLoanSteps] = useState<WorkflowDefinitionStep[]>([]);
  const [booking, setBooking] = useState<Record<string, any> | null>(null);
  const [bookingHistoryCount, setBookingHistoryCount] = useState(0);
  const [customers, setCustomers] = useState<CustomerDoc[]>([]);
  const [txns, setTxns] = useState<BankTransactionRow[]>([]);
  const [mainCase, setMainCase] = useState<WorkflowCase | null>(null);
  const [loanCase, setLoanCase] = useState<WorkflowCase | null>(null);
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDoc[]>([]);
  const [draftDocs, setDraftDocs] = useState<UploadedDoc[]>([]);
  const [docConfig, setDocConfig] = useState<{ uploadTypes: Array<Record<string, any>>; templates: Array<Record<string, any>> }>({ uploadTypes: [], templates: [] });
  const [changeOrders, setChangeOrders] = useState<ChangeOrderDoc[]>([]);
  const [bookingLoaded, setBookingLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Parts that failed to load, so cards can say so instead of showing an empty list as if it were true.
  const [failed, setFailed] = useState<{ project?: boolean; receipts?: boolean; documents?: boolean; changeOrders?: boolean }>({});
  const fail = useCallback((key: 'project' | 'receipts' | 'documents' | 'changeOrders', err: unknown) => {
    console.warn(`Unit page: load ${key} failed`, err);
    setFailed((f) => ({ ...f, [key]: true }));
  }, []);
  const [receiptsLoaded, setReceiptsLoaded] = useState(false);
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [changeOrdersLoaded, setChangeOrdersLoaded] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Units of the project: for this unit and the previous / next buttons.
  useEffect(() => {
    setUnitsLoaded(false);
    if (!projectId) return;
    return onSnapshot(
      query(collection(db, 'projects', projectId, 'units'), orderBy('unitNumberLower', 'asc'), limit(5000)),
      (snap) => {
        setUnits(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as any) }))
            .filter((u: any) => u?.isActive !== false)
            .sort((a: any, b: any) => compareUnitNumbers(a?.unitNumber || a?.id, b?.unitNumber || b?.id))
        );
        setUnitsLoaded(true);
      },
      (err) => {
        console.error('Unit page: load units failed', err);
        setError("Couldn't load units. Refresh the page to try again.");
        setUnitsLoaded(true);
      }
    );
  }, [db, fail, projectId]);

  // Project settings and the office workflow definitions it points to.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const loadDefinition = async (id: string): Promise<WorkflowDefinitionStep[]> => {
      if (!id) return [];
      const own = await getDoc(doc(db, 'projects', projectId, 'workflowDefinitions', id));
      const snap = own.exists() ? own : await getDoc(doc(db, 'officeWorkflowDefinitions', id));
      return snap.exists() ? normalizeDefinitionSteps((snap.data() as any)?.steps || []) : [];
    };
    (async () => {
      const snap = await getDoc(doc(db, 'projects', projectId));
      const meta = snap.exists() ? (snap.data() as any) : null;
      if (cancelled) return;
      setProjectMeta(meta);
      const [main, loan] = await Promise.all([
        loadDefinition(String(meta?.officeMainWorkflowDefinitionId || '').trim()),
        loadDefinition(String(meta?.officeLoanWorkflowDefinitionId || '').trim()),
      ]);
      if (cancelled) return;
      setMainSteps(main);
      setLoanSteps(loan);
    })().catch((err) => fail('project', err));
    return () => {
      cancelled = true;
    };
  }, [db, fail, projectId]);

  const unit = useMemo(() => units.find((u) => u.id === unitId) || null, [unitId, units]);
  const bookingId = String(unit?.activeBookingId || '').trim();

  // Receipts and refunds for this unit, live.
  useEffect(() => {
    setTxns([]);
    setReceiptsLoaded(false);
    setFailed((f) => ({ ...f, receipts: false }));
    if (!projectId || !unitId) return;
    return onSnapshot(
      query(collection(db, 'projects', projectId, 'bankTransactions'), where('unitId', '==', unitId), limit(500)),
      (snap) => {
        setTxns(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })).filter((t: any) => t?.isActive !== false));
        setReceiptsLoaded(true);
      },
      (err) => {
        fail('receipts', err);
        setReceiptsLoaded(true);
      }
    );
  }, [db, fail, projectId, unitId]);

  // Workflow cases for the active booking, live so a step done in the drawer shows at once.
  useEffect(() => {
    setMainCase(null);
    setLoanCase(null);
    if (!projectId || !unitId || !bookingId) return;
    const active = (type: string) => (snap: any) => {
      const data = snap.exists() ? (snap.data() as WorkflowCase) : null;
      return data && String(data.caseType || '') === type && String(data.status || '') === 'active' ? data : null;
    };
    const u1 = onSnapshot(doc(db, 'projects', projectId, 'units', unitId, 'cases', bookingId), (s) => setMainCase(active('main')(s)), () => setMainCase(null));
    const u2 = onSnapshot(doc(db, 'projects', projectId, 'units', unitId, 'cases', `loan_${bookingId}`), (s) => setLoanCase(active('loan')(s)), () => setLoanCase(null));
    return () => {
      u1();
      u2();
    };
  }, [bookingId, db, projectId, unitId]);

  // Uploaded and generated documents of the active booking, live.
  useEffect(() => {
    setUploadedDocs([]);
    setDocsLoaded(false);
    setFailed((f) => ({ ...f, documents: false }));
    if (!projectId || !unitId || !bookingId) {
      setDocsLoaded(true);
      return;
    }
    return onSnapshot(
      query(collection(db, 'projects', projectId, 'units', unitId, 'bookings', bookingId, 'uploaded'), orderBy('createdAt', 'desc')),
      (snap) => {
        setUploadedDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setDocsLoaded(true);
      },
      (err) => {
        fail('documents', err);
        setDocsLoaded(true);
      }
    );
  }, [bookingId, db, fail, projectId, unitId]);

  // Document Config (upload types and generated templates): names and which group each document shows in.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    Promise.all([getDocs(collection(db, 'officeDocumentUploadTypes')), getDocs(collection(db, 'officeDocumentTemplates'))])
      .then(([types, tpls]) => {
        if (cancelled) return;
        const forProject = (row: any) => {
          const ids = Array.isArray(row?.assignedProjectIds) ? row.assignedProjectIds.map(String) : [];
          return !ids.length || ids.includes(String(projectId));
        };
        setDocConfig({
          uploadTypes: types.docs.map((d) => ({ id: d.id, ...(d.data() as any) })).filter(forProject),
          templates: tpls.docs.map((d) => ({ id: d.id, ...(d.data() as any) })),
        });
      })
      .catch((err) => console.warn('Unit page: load document config failed', err));
    return () => {
      cancelled = true;
    };
  }, [db, projectId]);

  // Generated drafts (including the booking form and payment details), live.
  useEffect(() => {
    setDraftDocs([]);
    if (!projectId || !unitId || !bookingId) return;
    return onSnapshot(
      collection(db, 'projects', projectId, 'units', unitId, 'bookings', bookingId, 'draft'),
      (snap) => setDraftDocs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      (err) => console.warn('Unit page: load drafts failed', err)
    );
  }, [bookingId, db, projectId, unitId]);

  // Booking form draft (picked the way the booking drawer picks it), whether the signed form is uploaded,
  // and the hover details the classic page shows.
  const bookingForm = useMemo(() => {
    const isForm = (r: UploadedDoc) =>
      r.id === 'booking_form_v1' || String(r.templateId || '').trim() === 'booking_form_v1' || String(r.type || '').trim().toLowerCase() === 'bookingformdraft';
    const row = draftDocs.find(isForm) || uploadedDocs.find((r) => r.id === 'bookingFormDraft' || isForm(r)) || null;
    const file = row?.files?.find((f) => f?.url) || null;
    const signed = uploadedDocs.some((r) => (r.id === 'booking_form_signed' || r.id === 'bookingFormSigned') && (r.files || []).some((f) => f?.url));
    const b: Record<string, any> = booking || {};
    const ymd = String(b.bookingFormDate || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    const entered = ymd ? `${ymd[3]}-${ymd[2]}-${ymd[1]}${b.bookingFormTime ? ` ${String(b.bookingFormTime).trim()}` : ''}` : '';
    const generatedMs = Number(file?.uploadedAtMs || 0);
    const generated = generatedMs
      ? new Date(generatedMs).toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
      : '';
    const rank = typeof b.rank === 'number' ? String(b.rank) : String(b.rank || '').trim();
    const details: Array<[string, string]> = file
      ? ([
          ['File', String(file.originalName || file.name || 'BOOKING FORM DRAFT')],
          ['Date/Time', entered || generated],
          ['Ref By', String(b.refByBrokerName || '').trim()],
          ['Rank', rank],
          ['Scheme', b.schemeEnabled ? 'Yes' : 'No'],
          ['Remark', String(b.schemeRemark || '').trim()],
        ] as Array<[string, string]>).filter(([, v]) => v)
      : [];
    return {
      // Download is only useful until the signed form is uploaded.
      file: file && !signed ? { url: String(file.url), name: String(file.originalName || file.name || 'BOOKING_FORM_DRAFT.pdf') } : null,
      signed,
      details,
    };
  }, [booking, draftDocs, uploadedDocs]);

  // Change orders, matched by unit id or (older rows) by unit number, as the booking drawer does.
  const unitNo = String(unit?.unitNumber || '').trim();
  useEffect(() => {
    setChangeOrders([]);
    setChangeOrdersLoaded(false);
    setFailed((f) => ({ ...f, changeOrders: false }));
    if (!projectId || !unitId) return;
    const byId = new Map<string, ChangeOrderDoc>();
    const byNo = new Map<string, ChangeOrderDoc>();
    const publish = () => {
      const merged = new Map<string, ChangeOrderDoc>();
      byNo.forEach((row, id) => merged.set(id, row));
      byId.forEach((row, id) => merged.set(id, row));
      setChangeOrders(Array.from(merged.values()).filter((r) => r.isActive !== false));
      setChangeOrdersLoaded(true);
    };
    const fill = (target: Map<string, ChangeOrderDoc>) => (snap: any) => {
      target.clear();
      snap.docs.forEach((d: any) => target.set(d.id, { id: d.id, ...(d.data() as any) }));
      publish();
    };
    const warn = (err: unknown) => {
      fail('changeOrders', err);
      setChangeOrdersLoaded(true);
    };
    const col = collection(db, 'projects', projectId, 'changeOrders');
    const unsubs = [onSnapshot(query(col, where('unitId', '==', unitId)), fill(byId), warn)];
    if (unitNo) unsubs.push(onSnapshot(query(col, where('unitNo', '==', unitNo)), fill(byNo), warn));
    return () => unsubs.forEach((u) => u());
  }, [db, fail, projectId, unitId, unitNo]);

  // Booking, its history count and its buyers (with KYC document counts).
  useEffect(() => {
    setBookingLoaded(false);
    setBooking(null);
    setCustomers([]);
    if (!projectId || !unitId || !bookingId) {
      setBookingLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const bookingsSnap = await getDocs(query(collection(db, 'projects', projectId, 'units', unitId, 'bookings'), limit(50)));
      const active = bookingsSnap.docs.find((d) => d.id === bookingId);
      const customersSnap = await getDocs(collection(db, 'projects', projectId, 'units', unitId, 'bookings', bookingId, 'customers'));
      const withKyc = await Promise.all(
        customersSnap.docs.map(async (c) => {
          const kyc = await getDocs(collection(db, 'projects', projectId, 'units', unitId, 'bookings', bookingId, 'customers', c.id, 'kycDocs'));
          return { id: c.id, ...(c.data() as any), kycCount: kyc.size } as CustomerDoc;
        })
      );
      if (cancelled) return;
      setBooking(active ? (active.data() as any) : null);
      setBookingHistoryCount(bookingsSnap.size);
      setCustomers(withKyc);
      setBookingLoaded(true);
    })().catch((err) => {
      if (cancelled) return;
      console.error('Unit page: load booking failed', err);
      setError("Couldn't load the booking. Refresh the page to try again.");
      setBookingLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [bookingId, db, projectId, reloadNonce, unitId]);

  const reload = useCallback(() => setReloadNonce((n) => n + 1), []);
  const loading = !unitsLoaded || (!!unit && (!bookingLoaded || !receiptsLoaded));

  return { loading, error, units, unit, projectMeta, booking, bookingHistoryCount, customers, txns, mainCase, loanCase, mainSteps, loanSteps, uploadedDocs, draftDocs, docConfig, bookingForm, changeOrders, docsLoaded, changeOrdersLoaded, failed, reload };
}

/**
 * Buyer name per unit for the unit switcher, the way the Units list finds it: a name saved on the unit,
 * else the primary customer of the active booking. Loaded once per project, only when asked for.
 */
const buyerNameCache = new Map<string, Promise<Map<string, string>>>();
export function loadBuyerNames(projectId: string, units: UnitDoc[]): Promise<Map<string, string>> {
  const cached = buyerNameCache.get(projectId);
  if (cached) return cached;
  const db = getFirestore();
  const job = (async () => {
    const out = new Map<string, string>();
    await Promise.all(
      units.map(async (u) => {
        const direct = String(u.primaryCustomerName || u.customerName || u.allotteeName || '').trim();
        if (direct) return void out.set(u.id, direct);
        const bookingId = String(u.activeBookingId || '').trim();
        if (!bookingId) return;
        const snap = await getDocs(collection(db, 'projects', projectId, 'units', u.id, 'bookings', bookingId, 'customers'));
        let primary = '';
        let first = '';
        snap.docs.forEach((d) => {
          const r = d.data() as any;
          const name = String(r.displayName || r.name || [r.firstName, r.middleName, r.surname].filter(Boolean).join(' ')).trim();
          if (!name) return;
          if (!primary && (r.isPrimary === true || String(r.role || '').trim().toLowerCase() === 'primary')) primary = name;
          if (!first) first = name;
        });
        if (primary || first) out.set(u.id, primary || first);
      })
    );
    return out;
  })();
  buyerNameCache.set(projectId, job);
  job.catch(() => buyerNameCache.delete(projectId));
  return job;
}
